<script>
export default {
    name: 'CardsPage',
};
</script>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import i18n from '@/plugins/i18n.ts';
import CrmLogo from '@/components/crm-logo.vue';
import BlockContainer from '@/components/block-container';
import notify from '@/components/base-components/components/notification/notify.js';
import useDocs from '@/composables/useDocs.ts';

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    fetch: {
        type: Function,
        required: true,
    },
});

const loading = ref(false);
const list = ref([]);

onBeforeMount(async () => {
    loading.value = true;

    try {
        list.value = await props.fetch();
    } catch {
        notify.error({
            title: i18n.t('tmp An error occurred'),
        });
    } finally {
        loading.value = false;
    }
});

const cards = computed(() => ([
    {
        title: props.title,
        list: list.value.filter(item => item?.type !== 'video'),
    },
    {
        title: 'Product Video Tutorials',
        list: list.value.filter(item => item?.type === 'video'),
    },
].filter(item => item.list.length > 0)));

const { open: openDocs } = useDocs();

function goTo(card) {
    if (card.docs) return openDocs(card.docs);

    window.open(card.url, '_blank');
}
</script>

<template>
    <div
        v-loader.immediate="loading"
        class="grid grid-cols-1 max-w-7xl gap-8 lg:grid-cols-3 mx-auto py-6 px-10"
    >
        <template v-for="group in cards">
            <div
                class="col-span-full hidden text-center text-3xl font-bold lg:mt-10 lg:block"
                :key="group.title"
            >
                {{ group.title }}
            </div>

            <template v-for="(card, index) in group.list">
                <base-video
                    v-if="card.type === 'video'"
                    preview-class="w-full rounded-xl"
                    :key="`${group.title}-${index}`"
                    :link="card.url"
                    :preview-link="card.preview"
                />
                <block-container
                    v-else
                    :key="`${group.title}-${index}`"
                    content-class="flex flex-col justify-between gap-y-3"
                >
                    <div class="space-y-2">
                        <div class="text-lg font-black">
                            {{ card.title }}
                        </div>
                        <div>
                            {{ card.message }}
                        </div>
                    </div>

                    <div class="flex items-end justify-between gap-x-3">
                        <base-button
                            class="self-start"
                            type="primary"
                            round
                            @click="goTo(card)"
                        >
                            {{ $t('read more') }}
                        </base-button>

                        <crm-logo v-if="card.platform" :crm="card.platform" class="h-10" />
                    </div>
                </block-container>
            </template>
        </template>
    </div>
</template>
